import { Button, IconButton, RadioGroup, FormControlLabel, Radio, Tab, Tabs } from '@material-ui/core';
import CancelIcon from '@material-ui/icons/Cancel';
import React, { useEffect, useReducer, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import CustomCheckbox from '../../../../../shared/form-controls/custom-checkbox/Custom-Checkbox';
import CustomTextField from '../../../../../shared/form-controls/custom-text-field/Custom-Text-Field';
import CustomSingleSelectCrud from '../../../../../shared/form-controls/custom-single-select-crud/CustomSingleSelectCrud';
import { formDataValidate } from '../../../../../utils/formUtils';
import { notificationManager } from '../../../../../utils/NotificationUtils';
import {
  createNewPersonalisation,
  updateHotelPersonalisation,
  getPersonalisationCategoriesAPI,
  createPersonalisationCategoryAPI,
  updatePersonalisationCategoryAPI,
  deletePersonalisationCategoryAPI,
} from '../../../../../utils/ServicesAPIUtils';
import { allHotelsSelector, fetchAllAccommodations } from '../../../../property/propertySlice';
import { FormControl } from '@mui/material';
import { decryptLocalStorage } from '../../../../../utils/authUtils';
import { stepDownLoaderCount, stepUpLoaderCount } from '../../../../../utils/loaderSlice';
import ImageUpload from '../../../../../shared/image-upload/ImageUpload';
import { getProcessedImages } from '../../../../../shared/image-upload/ImageProcessor';
import { ASSET_PACKAGES } from '../../../../../constants/PropertyConst';
import LinearProgressBar from '../../../../../shared/progress-bar/Progress';

const PersonalisationCreate = (props) => {
  const dispatch = useDispatch();
  const { toggleDrawer, personalisation, hotelId, showLoader = () => {} } = props;
  const originalHotels = useSelector(allHotelsSelector);
  const groupId = originalHotels?.find((h) => h?.id === hotelId)?.groupId;
  const [isTabInValid, setIsTabInValid] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const [rawImages, setRawImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [personalisationCategories, setPersonalisationCategories] = useState([]);
  const deletingCategoryIdsRef = useRef(new Set());
  const [formData, setFormData] = useReducer((state, newState) => ({ ...state, ...newState }), {
    name: {
      value: '',
      fieldName: 'Personalisation name',
      errors: {},
      validations: ['required'],
      tab: 0,
    },
    code: {
      value: '',
      fieldName: 'Personalisation code',
      errors: {},
      validations: ['required', 'validAlphaNumeric'],
      tab: 0,
    },
    description: {
      value: '',
      fieldName: 'Description',
      errors: {},
      validations: ['required'],
      tab: 0,
    },
    cost: {
      value: '',
      fieldName: 'Cost',
      errors: {},
      validations: ['required', 'positiveOnlyWithZero'],
      tab: 0,
    },
    unit: {
      value: '',
      fieldName: 'Unit',
      errors: {},
      validations: ['required'],
      placeholder: 'Ex: Per Bottle, Per Bouquet',
      tab: 0,
    },
    isActive: {
      value: true,
      selected: true,
      fieldName: 'Active',
      errors: {},
      validations: [],
      tab: 0,
    },
    maxQuantity: {
      value: 'inactive',
      fieldName: 'maxQuantity',
      validations: [],
      tab: 0,
    },
    maxQuantityNum: {
      value: '',
      fieldName: 'Max Quantity',
      errors: {},
      validations: ['required'],
      tab: 0,
    },
    personalisationCategory: {
      value: null,
      fieldName: 'Personalisation Category',
      errors: {},
      validations: [],
      tab: 0,
    },
    images: {
      value: [],
      fieldName: 'Images',
      errors: {},
      validations: [],
      tab: 1,
    },
  });

  const fetchCategories = async () => {
    try {
      const res = await getPersonalisationCategoriesAPI(hotelId);
      setPersonalisationCategories(res?.data?.getPersonalisationCategories || []);
    } catch (error) {
      console.log('Failed to fetch personalisation categories', error);
    }
  };

  const handleCreateCategory = async (categoryName) => {
    try {
      dispatch(stepUpLoaderCount());
      const res = await createPersonalisationCategoryAPI(hotelId, categoryName);
      if (res?.data?.createPersonalisationCategory?.id) {
        notificationManager('success', 'Personalisation category created successfully');
        fetchCategories();
      }
    } catch (error) {
      if (error?.graphQLErrors) {
        notificationManager('danger', error?.graphQLErrors?.[0]?.message);
      } else {
        notificationManager('danger', 'Failed to create personalisation category');
      }
    } finally {
      dispatch(stepDownLoaderCount());
    }
  };

  const handleEditCategory = async (categoryName, id) => {
    try {
      dispatch(stepUpLoaderCount());
      const res = await updatePersonalisationCategoryAPI(hotelId, categoryName, id);
      if (res?.data?.updatePersonalisationCategory?.id) {
        notificationManager('success', 'Personalisation category updated successfully');
        fetchCategories();
      }
    } catch (error) {
      if (error?.graphQLErrors) {
        notificationManager('danger', error?.graphQLErrors?.[0]?.message);
      } else {
        notificationManager('danger', 'Failed to update personalisation category');
      }
    } finally {
      dispatch(stepDownLoaderCount());
    }
  };

  const handleDeleteCategory = async (id) => {
    if (deletingCategoryIdsRef?.current?.has(id)) return;
    deletingCategoryIdsRef?.current?.add(id);
    try {
      dispatch(stepUpLoaderCount());
      const res = await deletePersonalisationCategoryAPI(hotelId, id);
      if (res?.data?.deletePersonalisationCategory?.status) {
        notificationManager('success', res?.data?.deletePersonalisationCategory?.message);
        fetchCategories();
        if (formData?.personalisationCategory?.value?.id === id) {
          setFormData({
            personalisationCategory: {
              ...formData.personalisationCategory,
              value: null,
              errors: {},
            },
          });
        }
      }
    } catch (error) {
      if (error?.graphQLErrors) {
        notificationManager('danger', error?.graphQLErrors?.[0]?.message);
      } else {
        notificationManager('danger', 'Failed to delete personalisation category');
      }
    } finally {
      deletingCategoryIdsRef?.current?.delete(id);
      dispatch(stepDownLoaderCount());
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    if (personalisation) {
      const currFields = { ...formData };
      currFields.name.value = personalisation.name;
      currFields.code.value = personalisation.code;
      currFields.unit.value = personalisation.unit;
      currFields.cost.value = personalisation.cost;
      currFields.isActive.selected = personalisation.isActive;
      currFields.description.value = personalisation.description;
      currFields.isActive.value = personalisation.isActive;
      currFields.maxQuantity.value = personalisation.maxQuantity.status;
      currFields.maxQuantityNum.value = personalisation.maxQuantity.maxQuantityValue;
      if (personalisation.personalisationCategory) {
        currFields.personalisationCategory.value = personalisation.personalisationCategory;
      }
      currFields.images.value = personalisation.images || [];
      setFormData(currFields);
    }
  }, [personalisation]);

  useEffect(() => {
    const isActive = formData?.maxQuantity?.value === 'active';
    const current = formData?.maxQuantityNum?.value;
    setFormData({
      maxQuantityNum: {
        ...formData?.maxQuantityNum,
        value: isActive
          ? current !== undefined && current !== null && String(current)?.trim() !== ''
            ? current
            : 1
          : 1,
        validations: isActive ? ['required'] : [],
      },
    });
  }, [formData?.maxQuantity?.value]);

  const onImageListUpdated = (imgObjArr) => {
    setRawImages(imgObjArr);
  };

  const onUploadedImageRemoval = (index) => {
    const images = [...formData.images.value];
    images.splice(index, 1);
    setFormData({ images: { ...formData.images, value: images } });
  };

  const validate = (tab) => {
    const updatedFields = {};
    const currTabFormData = Object.fromEntries(
      Object.entries(formData).filter(([key, field]) => field.tab === tab),
    );
    const isValid = formDataValidate(currTabFormData, updatedFields);
    setFormData(updatedFields);
    return isValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validate(0)) {
      try {
        dispatch(stepUpLoaderCount());
        setLoading(true);
        toggleDrawer('', false, '');
        const selectedLanguage = decryptLocalStorage('lang');
        const language = selectedLanguage !== 'en' ? selectedLanguage : '';
        const personalisationCategoryPayload = formData.personalisationCategory.value
          ? {
              categoryName: formData.personalisationCategory.value.categoryName,
              id: formData.personalisationCategory.value.id,
            }
          : null;

        const images = await getProcessedImages(
          rawImages,
          groupId,
          ASSET_PACKAGES,
          hotelId,
        );

        if (personalisation) {
          await updateHotelPersonalisation(
            hotelId,
            {
              code: formData.code.value,
              cost: formData.cost.value,
              description: formData.description.value,
              isActive: formData.isActive.value,
              name: formData.name.value,
              unit: formData.unit.value,
              maxQuantity: {
                maxQuantityValue: formData.maxQuantityNum.value,
                status: formData.maxQuantity.value,
              },
              personalisationCategory: personalisationCategoryPayload,
              images,
              id: personalisation.id,
            },
            language,
          );
          notificationManager('success', 'Personalisation updated successfully');
        } else {
          await createNewPersonalisation(
            hotelId,
            {
              code: formData.code.value,
              cost: formData.cost.value,
              description: formData.description.value,
              isActive: formData.isActive.value,
              name: formData.name.value,
              unit: formData.unit.value,
              maxQuantity: {
                maxQuantityValue: formData.maxQuantityNum.value,
                status: formData.maxQuantity.value,
              },
              personalisationCategory: personalisationCategoryPayload,
              images,
            },
            language,
          );
          notificationManager('success', 'Personalisation created successfully');
        }
        dispatch(fetchAllAccommodations(hotelId));
      } catch (error) {
        if (error?.graphQLErrors) {
          notificationManager('danger', error?.graphQLErrors?.[0]?.message);
        } else {
          personalisation
            ? notificationManager('danger', 'Personalisation updation failed')
            : notificationManager('danger', 'Personalisation creation failed');
        }
      } finally {
        setLoading(false);
        dispatch(stepDownLoaderCount());
      }
    } else {
      notificationManager('danger', 'Enter all required fields');
      setTabValue(0);
    }
  };

  const onCancel = (e) => {
    toggleDrawer('', false, '');
  };

  const handleTabChange = (event, newValue) => {
    if (newValue < tabValue) {
      setTabValue(newValue);
    } else if (validate(tabValue)) {
      setTabValue(newValue);
    }
  };

  const onNextClick = () => {
    if (validate(tabValue)) {
      setTabValue(tabValue + 1);
    }
  };

  const onMaxQuantityChange = (e) => {
    const maxQuantity = {
      value: e.target.value,
      fieldName: 'maxQuantity',
      validations: [],
      tab: 0,
    };
    setFormData({ maxQuantity });
  };

  useEffect(() => {
    const currTabFormData = Object.fromEntries(
      Object.entries(formData).filter(([key, field]) => field.tab === tabValue),
    );
    const isValid = formDataValidate(currTabFormData, false);
    setIsTabInValid(!isValid);
  }, [formData, tabValue]);

  return (
    <form className='roomtype-create' noValidate onSubmit={handleSubmit}>
      <div className='container pt-4 d-flex align-items-end flex-column '>
        <div className='row px-3 w-100 mt-4'>
          <div className='col-md-4'>
            <span className='page-title'>
              {personalisation ? 'Edit Personalisation' : 'Add Personalisation'}
            </span>
          </div>
          <div className='col-md-7'>
            <Tabs
              value={tabValue}
              variant='fullWidth'
              indicatorColor='primary'
              onChange={handleTabChange}
              aria-label='personalisation-add-tabs'
              textColor='primary'
              className='property-tabs'
            >
              <Tab label='Details' />
              <Tab label='Media' />
            </Tabs>
          </div>
          <div className='col-md-1 d-flex justify-content-end'>
            <IconButton aria-label='delete' onClick={(e) => onCancel(e)}>
              <CancelIcon />
            </IconButton>
          </div>
        </div>
        <div className='row px-3 w-100 d-flex mt-4 form-scroll'>
          {tabValue === 0 && (
            <>
              <div className='row px-3 w-100 mt-3 mb-2'>
                <div className='col-md-12'>
                  <span className='page-title-secondary'>Details</span>
                </div>
              </div>
              <div className='row px-3 w-100 d-flex mt-2'>
                <div className='col-md-6 mb-3'>
                  <CustomTextField
                    item={formData.name}
                    handleInputChange={(e) => {
                      setFormData({ name: e });
                    }}
                    maxLength={100}
                    endAdornment={true}
                    characterCount={true}
                    multiline={true}
                  />
                </div>
                <div className='col-md-6 mb-3'>
                  <CustomTextField
                    item={formData.code}
                    handleInputChange={(e) => {
                      setFormData({ code: e });
                    }}
                    maxLength={15}
                    endAdornment={true}
                    characterCount={true}
                    multiline={true}
                  />
                </div>
              </div>
              <div className='row px-3 w-100 d-flex mt-2'>
                <div className='col-md-12 mb-3'>
                  <CustomTextField
                    item={formData.description}
                    handleInputChange={(e) => {
                      setFormData({ description: e });
                    }}
                    multiline={true}
                    maxLength={1000}
                  />
                </div>
              </div>
              <div className='row px-3 w-100 d-flex mt-2'>
                <div className='col-md-6 mb-3'>
                  <CustomTextField
                    item={formData.cost}
                    handleInputChange={(e) => {
                      setFormData({ cost: e });
                    }}
                    type='number'
                    min={0}
                    max={99999999}
                    stripZeroOnSpinner
                  />
                </div>
                <div className='col-md-6 mb-3'>
                  <CustomTextField
                    item={formData.unit}
                    handleInputChange={(e) => {
                      setFormData({ unit: e });
                    }}
                    multiline={true}
                  />
                </div>
              </div>
              <div className='row px-3 w-100 d-flex mt-2'>
                <div className='col-md-6 mb-3'>
                  <CustomSingleSelectCrud
                    item={formData.personalisationCategory}
                    options={personalisationCategories}
                    handleCreate={handleCreateCategory}
                    handleEditTag={handleEditCategory}
                    handleDeleteTag={handleDeleteCategory}
                    classNameProps={'ellipsisText'}
                    labelField='categoryName'
                    valueField='id'
                    handleInputChange={(value) =>
                      setFormData({
                        personalisationCategory: {
                          ...formData.personalisationCategory,
                          value,
                          errors: {},
                        },
                      })
                    }
                  />
                </div>
              </div>
              <div className='row px-3 w-100 d-flex mt-2'>
                <div className='col-md-6 mb-3'>
                  {/* <FormControlLabel control={<Checkbox defaultChecked color="primary" />} label="Active" /> */}
                  <CustomCheckbox
                    item={formData.isActive}
                    handleInputChange={(e) => {
                      setFormData({ isActive: e });
                    }}
                    color='primary'
                  />
                </div>
              </div>
              <div className='row px-3 w-100 d-flex mt-2'>
                <div className='col-md-6 mt-4'>
                  <div className='align-items-center'>
                    <h4 className='tab-title'>Max Quantity</h4>
                    <div className='d-flex justify-content-between'>
                      <FormControl>
                        <RadioGroup
                          row
                          aria-labelledby='demo-radio-buttons-group-label'
                          defaultValue={formData.maxQuantity.value}
                          value={formData.maxQuantity.value}
                          name='radio-buttons-group'
                          onChange={(e) => onMaxQuantityChange(e)}
                        >
                          <FormControlLabel value='inactive' control={<Radio />} label='Inactive' />
                          <FormControlLabel
                            value='active'
                            control={<Radio className='radio' />}
                            label='Active'
                            className='ms-5'
                          />
                        </RadioGroup>
                      </FormControl>
                    </div>
                    {formData.maxQuantity.value === 'active' && (
                      <CustomTextField
                        item={formData.maxQuantityNum}
                        handleInputChange={(e) => {
                          setFormData({ maxQuantityNum: e });
                        }}
                        min={1}
                        type='number'
                        stripZeroOnSpinner
                      />
                    )}
                  </div>
                </div>
              </div>
            </>
          )}
          {tabValue === 1 && (
            <div className='row px-3 w-100 d-flex mt-4'>
              <div className='col-md-12 mb-3'>
                <ImageUpload
                  uploadedImages={formData.images.value}
                  imgList={rawImages}
                  onImageListUpdated={onImageListUpdated}
                  onUploadedImageRemoval={onUploadedImageRemoval}
                  onImagesLoading={(status) => {
                    setLoading(status);
                  }}
                  hotelId={hotelId}
                  groupId={groupId}
                  propertyType={ASSET_PACKAGES}
                  enabledMediaTabs={['image']}
                />
              </div>
            </div>
          )}
        </div>
        <div className='row mx-2 mt-auto px-4 w-100 pb-5'>
          <div className='col-md-12 d-flex justify-content-start'>
            {tabValue < 1 ? (
              <Button
                variant='contained'
                color='primary'
                onClick={onNextClick}
                size='large'
                className='me-3'
                disabled={isTabInValid}
              >
                Next
              </Button>
            ) : (
              <Button
                disabled={loading}
                onClick={handleSubmit}
                variant='contained'
                color='primary'
                className='me-3'
                size='large'
              >
                {personalisation ? 'Update' : 'Add'}
              </Button>
            )}
            <Button
              disabled={loading}
              variant='outlined'
              type='button'
              color='secondary'
              size='large'
              onClick={(e) => onCancel(e)}
            >
              Cancel
            </Button>
          </div>
          {loading && <div className='pt-4 col-md-12'>{<LinearProgressBar />}</div>}
        </div>
      </div>
    </form>
  );
};

export default PersonalisationCreate;
